import {RESP} from '@/_metronic/helpers/network/network-consts'
import {FC, useEffect, useMemo, useState} from 'react'
import SVG from 'react-inlinesvg'
import {Link, useParams} from 'react-router-dom'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import {Outlet} from '../outlet/core/_models'
import {getOutletById} from '../outlet/core/_requests'
import BookingForm from './components/BookingForm'
import {FormStateProvider, useBookingFormState} from './core/FormStateProvider'
import {ModalProvider, useSelectTreatmentModal} from './core/_ModalProvider'
import {createOrder} from './core/_requests'

/** ------- helpers: WIB time + hours check (10:00 <= t < 22:00) ------- */
const TARGET_OUTLET_ID = '75d9b555-26ce-49af-bbf0-935bf9c9624f'

const nowInJakarta = () => {
  const now = new Date()
  // Build a WIB Date reliably using formatToParts to avoid local-tz drift
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Jakarta',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(now)
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]))
  // Asia/Jakarta = UTC+07:00 (no DST)
  return new Date(
    `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}+07:00`
  )
}

const withinOperatingHours = (d: Date) => {
  const h = d.getHours()
  const m = d.getMinutes()
  // 10:00 inclusive, 22:00 exclusive
  const afterOpen = h > 10 || (h === 10 && m >= 0)
  const beforeClose = h < 22
  return afterOpen && beforeClose
}

/** ------- closed component ------- */
const ClosedCard: FC<{outletName?: string}> = ({outletName}) => {
  return (
    <div className='col-12 col-md-8 col-lg-6'>
      <div className='card py-4 px-5 shadow-wit-2 bg-illustration-order position-relative'>
        {/* Top illustration — responsive */}
        <img
          src={toAbsoluteUrl('/media/illustrations/sigma-1/5.png')}
          alt='Closed illustration'
          className='img-fluid d-block mx-auto mb-4 w-50 w-md-75 w-lg-100'
        />

        <div className='text-center'>
          <h1 className='fw-bolder mt-2'>We’re Closed</h1>
          <p className='text-gray-600 mt-2'>
            <SVG
              src={toAbsoluteUrl('/media/svg/general/lock.svg')}
              className='me-2 align-text-top'
            />
            <b className='text-gray-800'>Outlet {outletName ?? ''}</b> is currently closed.
          </p>
          <p className='text-gray-700 mb-0'>Operating hours: 10:00 AM – 10:00 PM (WIB)</p>
          <small className='text-gray-500'>Please come back during our operating hours.</small>
        </div>

        <img
          src={toAbsoluteUrl('/media/illustrations/Ilustration_order.png')}
          className='d-none d-md-block img-fluid position-absolute backdrop-order bottom-0 z-9'
          style={{width: 200}}
          alt=''
          aria-hidden='true'
        />
      </div>
    </div>
  )
}

const OrderPage: FC = () => {
  const {id} = useParams()
  const [outlet, setOutlet] = useState<Outlet | undefined>(undefined)
  const {showSuccessModal} = useSelectTreatmentModal()
  const {setIsLoading} = useBookingFormState()

  const getOutletData = async () => {
    try {
      const res = await getOutletById(id)
      if (res?.response?.code === RESP.SUCCESS) {
        setOutlet(res?.response?.data)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (id) getOutletData()
  }, [id])

  const handleCreateOrder = async (body: any) => {
    try {
      const res = await createOrder(body)
      if (res?.response?.code === RESP.SUCCESS) {
        showSuccessModal()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Compute gate once per render; if you need live ticking, add an interval.
  const shouldEnforceHours = id === TARGET_OUTLET_ID
  const openNow = useMemo(() => withinOperatingHours(nowInJakarta()), [])

  return (
    <>
      <div className='container z-10'>
        <div className='row justify-content-center my-5 gap-5'>
          {/* logos */}
          <div className='col-12 col-md-8 col-lg-6'>
            <div className='row'>
              <div className='col'>
                <img src={toAbsoluteUrl('/media/logos/OIC-brands.png')} className='img-fluid' />
              </div>
              {/* <div className='col'>
                <img
                  src={toAbsoluteUrl('/media/logos/logo-square-fitness.png')}
                  className='img-fluid'
                />
              </div>
              <div className='col'>
                <img src={toAbsoluteUrl('/media/logos/logo-alaya-new.png')} className='img-fluid' />
              </div> */}
            </div>
          </div>

          {/* gated content */}
          {!shouldEnforceHours || openNow ? (
            <div className='col-12 col-md-8 col-lg-6'>
              <div className='card py-4 px-5 shadow-wit-2 bg-illustration-order'>
                <div className='text-center'>
                  <h1 className='fw-bolder mt-4'>Booking Reservation</h1>
                  <p className='text-gray-600'>
                    <SVG src={toAbsoluteUrl('/media/svg/orders/location_tick.svg')} /> {outlet?.sub_brand_name} at <b className='text-gray-800'>{outlet?.name}</b>
                  </p>
                </div>
                {Boolean(id) && (
                  <BookingForm outlet_id={id || ''} handleSubmit={handleCreateOrder} />
                )}
                <Link
                  to='/privacy-policy'
                  className='w-100 text-center text-primary mt-3 fs-5 fw-bold'
                >
                  Privacy Policy
                </Link>
                <img
                  src={toAbsoluteUrl('/media/illustrations/Ilustration_order.png')}
                  className='d-none d-md-block w-200px img-fluid position-absolute backdrop-order bottom-0 z-9'
                />
              </div>
            </div>
          ) : (
            <ClosedCard outletName={outlet?.name} />
          )}
        </div>
      </div>
    </>
  )
}

const OrderWrapper: FC = () => {
  return (
    <>
      <FormStateProvider>
        <ModalProvider>
          <OrderPage />
        </ModalProvider>
      </FormStateProvider>
    </>
  )
}

export default OrderWrapper
